import * as React from 'react';
import * as styles from '../../help/help.module.scss';
import CircularProgress from '@material-ui/core/CircularProgress';
import SearchBar from '../../common/search-bar';
import useSearch from '../../../hooks/useSearch';
import useHelp from '../../../hooks/useHelp';
import useHelpDocuments from '../../../hooks/useHelpDocuments';
import HelpSection from './help-section';
import { useParams, Redirect } from 'react-router-dom';
import SecondaryButton from '../../common/secondary-button';
import PriorityTwoFailure from '../../announcement/priority-two-failure';
import PriorityTwoSuccess from '../../announcement/priority-two-success';
import useClient from '../../../hooks/useClient';
import TertiaryButton from '../../common/tertiary-button';
import ModalDialog from '../../modal-dialog/modalDialog';
import useModal from '../../../hooks/useModal';
import ClientMapModalContent from './client-map-modal-content';
import Paths from '../../../models/enums/paths';

/**
 * Outer component for the help page
 */
const ClientHelpDocuments = () => {
  const {
    loading,
    fetchHelpDocumentsWithRevisions,
    fetchGlobalHelpDocuments,
    globalHelpDocuments,
    helpDocuments,
    errorMessage,
    successMessage,
    dispatch
  } = useHelpDocuments();
  const { fetchAllClients } = useClient();
  const { searchQuery, updateSearchQuery, isSearching, setIsSearching } = useSearch();
  const [redirectToAddDocument, setRedirectToAddDocument] = React.useState(false);
  const { clientId } = useParams();
  const { modalIsOpen, openModal, closeModal } = useModal();
  const { documentsWithoutRoot } = useHelp();

  React.useEffect(() => {
    dispatch({ loading: true });
    if (!clientId) {
      Promise.all([fetchGlobalHelpDocuments(), fetchAllClients()]).then(([helpDocuments, clientList]) => {
        dispatch({ clientList: clientList.results, loading: false });
      });
    } else {
      Promise.all([fetchHelpDocumentsWithRevisions(), fetchGlobalHelpDocuments(false, true)]).then(() =>
        dispatch({ loading: false })
      );
    }
  }, []);

  if (redirectToAddDocument) {
    if (!clientId) {
      return <Redirect push to={{ pathname: Paths.AddGlobalHelpDocument }} />;
    } else {
      return <Redirect push to={{ pathname: Paths.AddClientHelpDocument.replace(':clientId', clientId) }} />;
    }
  }

  const mappedDocuments = helpDocuments.rootId
    ? documentsWithoutRoot(helpDocuments)
        .filter(document => document.data.clientId === -1)
        .map(document => document.data)
    : [];

  return (
    <div className={styles.helpContainer}>
      {errorMessage && <PriorityTwoFailure message={errorMessage} />}
      {successMessage && <PriorityTwoSuccess message={successMessage} />}
      {clientId && (
        <ModalDialog
          isOpen={modalIsOpen}
          onClose={closeModal}
          title={'Map Document to Clients'}
          width="345px"
          children={
            clientId && (
              <ClientMapModalContent
                globalHelpDocuments={globalHelpDocuments}
                mappedDocuments={mappedDocuments}
                closeModal={closeModal}
              />
            )
          }
        />
      )}
      <div className={styles.header}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', paddingRight: '10px' }}>
            <h1>{!clientId ? 'Global ' : ''}Help Documentation</h1>
            <SecondaryButton
              text="Add a Document"
              title="Add a Document"
              handleClick={() => setRedirectToAddDocument(true)}
            />
          </div>
          {clientId && <TertiaryButton text="Document Mapping" handleClick={openModal} />}
        </div>
        <SearchBar
          value={searchQuery}
          updateValue={(val: string) => updateSearchQuery(val)}
          setIsSearching={(searching: boolean) => setIsSearching(searching)}
          placeholder={'Help Documentation'}
        />
      </div>
      {loading ? (
        <div className={styles.circularProgress}>
          <CircularProgress />
        </div>
      ) : (
        <HelpSection
          searchQuery={searchQuery}
          isSearching={isSearching}
          setIsSearching={(searching: boolean) => setIsSearching(searching)}
        />
      )}
    </div>
  );
};

export default ClientHelpDocuments;
